/**
 * Tiny gtag wrapper for the inkling A/B test.
 *
 * The measurement ID is injected at build time via Vite env var
 * `VITE_GA_MEASUREMENT_ID`. When the var is absent (local dev, preview
 * deploys without the secret), every call here is a silent no-op so the
 * page works identically without analytics.
 *
 * Conventions:
 *   - `lp_variant_assigned` fires once per page load with the assigned
 *     A/B variant. This is the denominator for the test.
 *   - `magic_link_requested` fires when the visitor submits the email
 *     form. This is the numerator.
 *   - Both events carry a `variant` parameter (A | B | C) so the
 *     conversion rate can be sliced by variant in GA4 explore reports.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = (import.meta as { env?: Record<string, string | undefined> }).env
  ?.VITE_GA_MEASUREMENT_ID;

export function gaEnabled(): boolean {
  return Boolean(measurementId) && typeof window !== "undefined" && typeof window.gtag === "function";
}

export function trackEvent(
  name: string,
  params: Record<string, string | number | boolean | undefined> = {},
): void {
  if (!gaEnabled()) return;
  window.gtag!("event", name, params);
}

export function trackVariantAssigned(variant: string): void {
  trackEvent("lp_variant_assigned", { variant });
}

export function trackMagicLinkRequested(variant: string): void {
  // Send_to keeps the conversion event linkable to the same property; GA4
  // also lets you mark this as a Conversion event in the property settings
  // so it shows up in the standard reports automatically.
  trackEvent("magic_link_requested", { variant });
}

export function trackMagicLinkVerified(variant: string | null): void {
  // The "real" conversion: visitor actually clicked the email and signed in.
  // Variant comes from the verified JWT, so it's tamper-evident attribution
  // even across devices (request on desktop → click on phone).
  trackEvent("magic_link_verified", { variant: variant ?? "unknown" });
}
